import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Megaphone, Save, Newspaper, Target, Star, Calendar, Lightbulb, BarChart2,
    DollarSign, ShieldQuestion, Download, Copy, FileText, WandSparkles,
    Users, Edit3, MessageSquare, Plus, Trash2, Handshake, BrainCircuit,
    ExternalLink, CheckCircle, Layout, Loader2, Lock, ChevronRight, Share2, Search
} from 'lucide-react';
import { doc, collection, addDoc, getDoc, getDocs, updateDoc, deleteDoc, query, where } from 'firebase/firestore';

// Sub-components 
import PressKitBuilderModal from './PressKitBuilderModal';

// Shared UI 
import {
    AppButton,
    BlackboxLoaderIcon,
    InfoCard
} from '../../components/SharedUI';

// Services 
import {
    generateInfluencerListLLM,
    callGeminiApi
} from '../../services/aiService';
import { PLATFORMS, GEMINI_2_5_PRO_MODEL } from '../../config/constants';

/**
 * Updates an influencer record in the Firestore database.
 * This is used by the InfluencerHub for logging contacts/status changes.
 */
const updateInfluencerInDB = async (influencerId, updatedInfluencer, db) => {
    try {
        const influencerDocRef = doc(db, 'influencers', influencerId);
        await updateDoc(influencerDocRef, updatedInfluencer);
        return true;
    } catch (error) {
        console.error('Error updating influencer:', error);
        throw error;
    }
};

// --- STYLED COMPONENTS (Matching Audience Genius) ---

const TacticalTab = ({ active, onClick, icon: Icon, label, count }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all whitespace-nowrap border ${
            active 
            ? 'bg-white text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.3)]' 
            : 'bg-black/40 text-zinc-500 border-zinc-800 hover:border-zinc-600 hover:text-zinc-300'
        }`}
    >
        <Icon size={14} />
        {label}
        {count !== undefined && (
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-black text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                {count}
            </span>
        )}
    </button>
);

const SectionHeader = ({ title, icon: Icon, action }) => (
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center text-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.1)]">
                <Icon size={16} />
            </div>
            <h2 className="text-xl font-bold text-white font-poppins tracking-tight">{title}</h2>
        </div>
        {action}
    </div>
);

// --- PREMIUM OVERLAY ---
const PremiumPressOverlay = ({ isVisible, mode, currentStepIndex }) => {
    if (!isVisible) return null;

    const strategySteps = [
        "Analyzing Global Market Trends...",
        "Evaluating Film-Specific Press Angles...",
        "Identifying Tier 1 Media Opportunities...",
        "Generating Chronological Timeline...",
        "Finalizing mi Press Dossier..."
    ];

    const influencerSteps = [
        "Scanning Social Psychographics...",
        "Querying Influencer Databases...",
        "Identifying High-Engagement Partners...",
        "Drafting Collaboration Hypotheses...",
        "Finalizing Influencer CRM Sync..."
    ];

    const steps = mode === 'strategy' ? strategySteps : influencerSteps;
    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className="absolute inset-0 bg-purple-500 blur-2xl opacity-20 animate-pulse rounded-full"></div>
                        <BlackboxLoaderIcon className="w-20 h-20 relative z-10 text-white" variant="white-pulse" />
                    </div>
                </div>

                <div className="relative w-full h-1 bg-zinc-800/50 rounded-full mb-8 overflow-hidden">
                    <div className="absolute inset-0 bg-purple-500/20 animate-pulse"></div>
                    <div 
                        className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500 bg-[length:200%_100%] animate-[shimmer_2s_infinite] transition-all duration-500 ease-out"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>

                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => {
                        const isDone = index < currentStepIndex;
                        const isCurrent = index === currentStepIndex;
                        return (
                            <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${isCurrent ? 'translate-x-2' : ''}`}>
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center border ${isDone ? 'border-purple-500 bg-purple-500/20 text-purple-400' : isCurrent ? 'border-white bg-white text-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                    {isDone && <CheckCircle size={10} />}
                                    {isCurrent && <Loader2 size={10} className="animate-spin" />}
                                </div>
                                <span className={isCurrent ? "text-white font-bold" : isDone ? "text-purple-300/50" : "text-zinc-700"}>
                                    {step}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- AIPressStrategyGenerator ---

const AIPressStrategyGenerator = ({ filmData, pressKitData, setNotification, pressStrategy, saveFilmData, credits, deductCredit }) => {
    const [loading, setLoading] = useState(false);
    const [activeStrategyTab, setActiveStrategyTab] = useState('executive'); // Changed default
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);

    // Audience Genius now captures releaseDate / releaseType / leadTimeWeeks directly —
    // that's the source of truth going forward. filmData.releaseWindow is the older
    // free-text field from project creation; kept only as a fallback for projects that
    // haven't re-run Audience Genius since these fields were added, so nobody has to
    // retype a release window that already exists elsewhere in the project.
    const resolveReleaseWindow = (filmData) => {
        if (filmData.releaseDate) {
            const parts = [new Date(filmData.releaseDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })];
            if (filmData.releaseType && filmData.releaseType !== 'TBD') parts.push(`(${filmData.releaseType})`);
            if (filmData.leadTimeWeeks != null && filmData.leadTimeWeeks !== '') parts.push(`— ${filmData.leadTimeWeeks} wks lead time`);
            return parts.join(' ');
        }
        if (filmData.releaseType && filmData.releaseType !== 'TBD') return filmData.releaseType;
        return filmData.releaseWindow || 'Not specified';
    };

    // Greenlight Coverage is optional (per the IA, it stays a standalone script-verdict
    // tool), so this degrades gracefully — the strategy just runs without a verdict
    // section if it hasn't been run yet.
    const summarizeGreenlight = (coverage) => {
        if (!coverage) return null;
        return {
            verdict: coverage.verdict,
            summary: coverage.the_verdict_summary,
            commercial_score: coverage.commercial_score,
            creative_score: coverage.creative_score,
            strengths: coverage.strengths,
            weaknesses: coverage.weaknesses,
            star_power_needed: coverage.star_power_needed
        };
    };

    // ... [Logic for generatePressStrategyLLM remains identical] ...
    const generatePressStrategyLLM = async (filmData, pressKitData) => {
        const releaseWindow = resolveReleaseWindow(filmData);
        const greenlight = summarizeGreenlight(filmData.greenlight_coverage);

        const prompt = `
You are a senior film publicist and press strategist with 20+ years of experience working with major studios and independent films. Based on the film data provided, create a comprehensive, actionable press strategy.

FILM DATA:
- Title: "${filmData.title}"
- Genre: "${filmData.genre}"
- Budget Level: "${filmData.budgetLevel}"
- Cast: "${filmData.stars}"
- Director: "${filmData.director}"
- Logline: "${filmData.logline}"
- Themes: "${filmData.themes}"
- Release Window: "${releaseWindow}"
${greenlight ? `
SCRIPT VERDICT (from Greenlight Coverage — weigh this into angle selection and outlet tier; a Red Light or Yellow Light script needs a press angle built around what's strong, not a generic pitch):
- Verdict: "${greenlight.verdict}" (Commercial ${greenlight.commercial_score}/100, Creative ${greenlight.creative_score}/100)
- Summary: "${greenlight.summary}"
- Strengths: ${JSON.stringify(greenlight.strengths || [])}
- Weaknesses: ${JSON.stringify(greenlight.weaknesses || [])}
- Star Power Needed: "${greenlight.star_power_needed?.verdict || 'Not assessed'}"` : `
SCRIPT VERDICT: Not yet run — Greenlight Coverage is optional, proceed without it.`}

PRESS KIT STATUS:
- Has poster: ${pressKitData.posterUrl ? 'Yes' : 'No'}
- Has synopsis: ${pressKitData.synopsis ? 'Yes' : 'No'}
- Featured cast/crew: ${(pressKitData.cast?.length || 0) + (pressKitData.crew?.length || 0)}
- Full credits: ${pressKitData.full_credits?.length || 0} people

CURRENT MARKET CONTEXT: Consider current film industry trends, awards season timing, and competitive landscape.

Generate a comprehensive press strategy that includes specific, actionable recommendations. Return valid JSON.`;

        const schema = {
            type: "OBJECT",
            properties: {
                executive_summary: {
                    type: "STRING",
                    description: "2-3 sentence top-level strategy summary"
                },
                strategy_pillars: {
                    type: "ARRAY",
                    items: {
                        type: "OBJECT",
                        properties: {
                            pillar: { type: "STRING" },
                            description: { type: "STRING" },
                            tactics: { type: "ARRAY", items: { type: "STRING" } }
                        },
                        required: ["pillar", "description", "tactics"]
                    }
                },
                target_outlets: {
                    type: "OBJECT",
                    properties: {
                        tier_1: {
                            type: "ARRAY",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    outlet: { type: "STRING" },
                                    contact_type: { type: "STRING" },
                                    angle: { type: "STRING" },
                                    priority: { type: "STRING", enum: ["High", "Medium", "Low"] }
                                },
                                required: ["outlet", "contact_type", "angle", "priority"]
                            }
                        },
                        tier_2: {
                            type: "ARRAY",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    outlet: { type: "STRING" },
                                    contact_type: { type: "STRING" },
                                    angle: { type: "STRING" },
                                    priority: { type: "STRING", enum: ["High", "Medium", "Low"] }
                                },
                                required: ["outlet", "contact_type", "angle", "priority"]
                            }
                        },
                        niche_specialized: {
                            type: "ARRAY",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    outlet: { type: "STRING" },
                                    contact_type: { type: "STRING" },
                                    angle: { type: "STRING" },
                                    priority: { type: "STRING", enum: ["High", "Medium", "Low"] }
                                },
                                required: ["outlet", "contact_type", "angle", "priority"]
                            }
                        }
                    }
                },
                timeline: {
                    type: "ARRAY",
                    description: "A chronological press strategy timeline with 3-5 distinct phases (e.g., Pre-Launch, Launch Week, Post-Launch).",
                    items: {
                        type: "OBJECT",
                        properties: {
                            phase: {
                                type: "STRING",
                                description: "The name of the marketing phase (e.g., 'Phase 1: Pre-Festival Buzz Building')."
                            },
                            timeframe: {
                                type: "STRING",
                                description: "The timing relative to release (e.g., 'T-12 to T-8 Weeks')."
                            },
                            activities: {
                                type: "ARRAY",
                                description: "A bulleted list of 3-5 key marketing activities for this phase.",
                                items: { type: "STRING" }
                            },
                            deliverables: {
                                type: "ARRAY",
                                description: "A bulleted list of 2-3 key press materials or assets to be produced during this phase.",
                                items: { type: "STRING" }
                            }
                        },
                        required: ["phase", "timeframe", "activities", "deliverables"]
                    }
                },
                press_angles: {
                    type: "ARRAY",
                    items: {
                        type: "OBJECT",
                        properties: {
                            angle: { type: "STRING" },
                            target_audience: { type: "STRING" },
                            key_message: { type: "STRING" },
                            supporting_materials: { type: "ARRAY", items: { type: "STRING" } }
                        },
                        required: ["angle", "target_audience", "key_message", "supporting_materials"]
                    }
                },
                success_metrics: {
                    type: "OBJECT",
                    properties: {
                        awareness_goals: { type: "ARRAY", items: { type: "STRING" } },
                        coverage_targets: { type: "ARRAY", items: { type: "STRING" } },
                        measurement_tools: { type: "ARRAY", items: { type: "STRING" } }
                    }
                },
                risk_mitigation: {
                    type: "ARRAY",
                    items: {
                        type: "OBJECT",
                        properties: {
                            risk: { type: "STRING" },
                            likelihood: { type: "STRING", enum: ["High", "Medium", "Low"] },
                            mitigation_strategy: { type: "STRING" }
                        },
                        required: ["risk", "likelihood", "mitigation_strategy"]
                    }
                },
                budget_allocation: {
                    type: "ARRAY",
                    items: {
                        type: "OBJECT",
                        properties: {
                            category: { type: "STRING" },
                            percentage: { type: "NUMBER" },
                            rationale: { type: "STRING" }
                        },
                        required: ["category", "percentage", "rationale"]
                    }
                }
            },
            required: ["executive_summary", "strategy_pillars", "target_outlets", "timeline", "press_angles", "success_metrics"]
        };

        return await callGeminiApi(prompt, {
            responseMimeType: "application/json",
            responseSchema: schema
        }, [], GEMINI_2_5_PRO_MODEL);
    };

    const handleGenerateStrategy = async () => {
        if (!filmData.title || !filmData.genre) {
            setNotification({ type: 'error', message: 'Please complete basic film information first.' });
            return;
        }
        if (credits < 5) { 
            setNotification({ type: 'error', message: 'Insufficient credits (5 required).' });
            return;
        }

        setLoading(true);
        setLoadingStepIndex(0);
        const stepInterval = setInterval(() => setLoadingStepIndex(prev => (prev < 4 ? prev + 1 : prev)), 2000);

        try {
            const result = await generatePressStrategyLLM(filmData, pressKitData);
            setLoadingStepIndex(4);
            await new Promise(r => setTimeout(r, 800));
            await deductCredit(5); 
            await saveFilmData({ ...filmData, press_strategy: result });
            setNotification({ type: 'success', message: 'mi press strategy generated!' });
        } catch (error) {
            setNotification({ type: 'error', message: `Failed: ${error.message}` });
        } finally {
            clearInterval(stepInterval);
            setLoading(false);
        }
    };

     const handleExportPDF = () => {
    try {
        if (!window.jspdf || !pressStrategy) {
            setNotification({ type: 'error', message: 'PDF generator not ready or no strategy data.' });
            return;
        }
        
        const { jsPDF } = window.jspdf;
        // Optimization: Use 'pt' (points) for finer typography control over 'mm'
        const doc = new jsPDF('p', 'pt', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 50;
        let y = margin;

        // --- STYLING CONSTANTS ---
        const COLORS = {
            PURPLE: [168, 85, 247],
            DARK: [24, 24, 27],
            GRAY: [113, 113, 122],
            LIGHT_BG: [244, 244, 245]
        };

        // --- UTILITIES ---
        const checkPageBreak = (needed) => {
            if (y + needed > pageHeight - margin) {
                doc.addPage();
                y = margin;
                return true;
            }
            return false;
        };

        const drawSectionHeader = (title) => {
            checkPageBreak(60);
            y += 20;
            doc.setFillColor(...COLORS.PURPLE);
            doc.rect(margin, y, 3, 18, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(...COLORS.DARK);
            doc.text(title.toUpperCase(), margin + 12, y + 14);
            y += 30;
        };

        // --- 1. COVER PAGE ---
        doc.setFillColor(...COLORS.DARK);
        doc.rect(0, 0, pageWidth, 120, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(28);
        doc.setFont("helvetica", "bold");
        doc.text("PRESS STRATEGY", margin, 65);
        
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.text(filmData.title?.toUpperCase() || "UNTITLED PROJECT", margin, 90);
        
        doc.setTextColor(...COLORS.GRAY);
        doc.text(`GENRE: ${filmData.genre || 'N/A'}  |  INTERNAL DOSSIER`, margin, 105);
        
        y = 160;

        // --- 2. EXECUTIVE SUMMARY ---
        drawSectionHeader("Executive Summary");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(...COLORS.DARK);
        const summary = doc.splitTextToSize(pressStrategy.executive_summary || "", pageWidth - (margin * 2));
        doc.text(summary, margin, y);
        y += (summary.length * 16) + 20;

        // --- 3. STRATEGY PILLARS (Grid Layout Simulation) ---
        drawSectionHeader("Strategy Pillars");
        pressStrategy.strategy_pillars?.forEach((p, i) => {
            const pillarLines = doc.splitTextToSize(p.description, pageWidth - (margin * 2) - 20);
            const blockHeight = (pillarLines.length * 14) + (p.tactics.length * 14) + 40;
            
            checkPageBreak(blockHeight);
            
            doc.setFillColor(...COLORS.LIGHT_BG);
            doc.roundedRect(margin, y, pageWidth - (margin * 2), blockHeight, 4, 4, 'F');
            
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.PURPLE);
            doc.text(`${i + 1}. ${p.pillar}`, margin + 15, y + 20);
            
            y += 35;
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...COLORS.DARK);
            doc.text(pillarLines, margin + 15, y);
            y += (pillarLines.length * 14) + 10;

            p.tactics.forEach(t => {
                doc.setTextColor(...COLORS.GRAY);
                doc.text("•", margin + 15, y);
                doc.setTextColor(...COLORS.DARK);
                doc.text(t, margin + 25, y);
                y += 14;
            });
            y += 20;
        });

        // --- 4. TARGET OUTLETS (Tiers) ---
        drawSectionHeader("Target Media Outlets");
        const renderTier = (outlets, label) => {
            if (!outlets || outlets.length === 0) return;
            checkPageBreak(40);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.GRAY);
            doc.text(label.toUpperCase(), margin, y);
            y += 15;

            outlets.forEach(o => {
                const angleLines = doc.splitTextToSize(o.angle, pageWidth - 250);
                const rowHeight = Math.max(30, (angleLines.length * 12) + 10);
                checkPageBreak(rowHeight);

                doc.setFont("helvetica", "bold");
                doc.setTextColor(...COLORS.DARK);
                doc.text(o.outlet, margin, y + 12);
                
                doc.setFont("helvetica", "normal");
                doc.setFontSize(9);
                doc.setTextColor(...COLORS.PURPLE);
                doc.text(o.priority, margin + 140, y + 12);
                
                doc.setTextColor(...COLORS.DARK);
                doc.text(angleLines, margin + 200, y + 12);
                
                y += rowHeight;
                doc.setDrawColor(...COLORS.LIGHT_BG);
                doc.line(margin, y, pageWidth - margin, y);
                y += 5;
            });
            y += 20;
        };

        renderTier(pressStrategy.target_outlets?.tier_1, "Tier 1: High Impact");
        renderTier(pressStrategy.target_outlets?.tier_2, "Tier 2: Trade & Specialty");

        // --- 5. TIMELINE (Phased approach) ---
        drawSectionHeader("Campaign Timeline");
        pressStrategy.timeline?.forEach((phase) => {
            checkPageBreak(100);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...COLORS.DARK);
            doc.text(phase.phase, margin, y);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(...COLORS.PURPLE);
            doc.text(phase.timeframe, pageWidth - margin, y, { align: 'right' });
            y += 15;
            
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(...COLORS.DARK);
            phase.activities.forEach(act => {
                doc.text(`- ${act}`, margin + 10, y);
                y += 14;
            });
            y += 15;
        });

        // --- FOOTER ---
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(...COLORS.GRAY);
            doc.text(`Blackbox mi Publicity Intelligence  |  Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
        }

        doc.save(`${filmData.title.replace(/\s+/g, '_')}_Press_Strategy.pdf`);
        setNotification({ type: 'success', message: 'Production-ready PDF generated.' });
    } catch (error) {
        console.error("PDF Export Failed:", error);
        setNotification({ type: 'error', message: 'Export failed. Check console for details.' });
    }
};


    const StrategyCard = ({ title, children, icon: Icon }) => (
        <div className="bg-zinc-900/50 p-6 rounded-xl border border-zinc-800 hover:border-purple-500/30 transition-all">
            <h4 className="text-sm font-bold text-zinc-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                {Icon && <Icon size={14} className="text-purple-500" />}
                {title}
            </h4>
            {children}
        </div>
    );

    const PriorityBadge = ({ priority }) => {
        const colors = {
            'High': 'text-red-400 border-red-500/30 bg-red-500/10',
            'Medium': 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10',
            'Low': 'text-green-400 border-green-500/30 bg-green-500/10'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${colors[priority] || colors.Medium}`}>
                {priority}
            </span>
        );
    };

    if (!pressStrategy) {
        const hasAudience = !!(filmData.ai_analyses && filmData.ai_analyses.length > 0);
        const hasGreenlight = !!filmData.greenlight_coverage;
        return (
            <>
                <PremiumPressOverlay isVisible={loading} mode="strategy" currentStepIndex={loadingStepIndex} />
                <div className="h-full flex flex-col items-center justify-center text-center p-12 bg-black/20 rounded-3xl border border-white/5 backdrop-blur-sm">
                    <div className="w-32 h-32 bg-zinc-900/50 rounded-full flex items-center justify-center mb-8 border border-zinc-800 shadow-2xl relative">
                        <div className="absolute inset-0 border border-purple-500/20 rounded-full animate-[ping_3s_infinite]" />
                        <Megaphone className="w-12 h-12 text-zinc-600"/>
                    </div>
                    <h3 className="text-3xl font-bold text-white font-poppins mb-4 tracking-tight">Strategy Not Initialized</h3>
                    <p className="text-zinc-500 max-w-md text-sm leading-relaxed mb-4">
                        Generate a comprehensive, AI-driven press strategy tailored to your film's specific genre, cast, and market positioning.
                    </p>
                    {/* Nothing to fill in here — this pulls straight from Audience Genius + Greenlight Coverage. */}
                    <div className="flex items-center gap-4 mb-8 text-[11px] font-mono uppercase tracking-wider">
                        <span className={hasAudience ? 'text-green-400' : 'text-zinc-600'}>
                            {hasAudience ? '● ' : '○ '}Audience Genius {hasAudience ? 'loaded' : 'not run'}
                        </span>
                        <span className={hasGreenlight ? 'text-green-400' : 'text-zinc-600'}>
                            {hasGreenlight ? '● ' : '○ '}Greenlight Coverage {hasGreenlight ? 'loaded' : 'skipped (optional)'}
                        </span>
                    </div>
                    <button 
                        onClick={handleGenerateStrategy} 
                        disabled={loading || !filmData.title} 
                        className="group relative px-8 py-4 bg-white text-black font-bold rounded-xl overflow-hidden transition-all hover:scale-[1.02] shadow-[0_0_30px_rgba(168,85,247,0.2)]"
                    >
                        <span className="relative z-10 flex items-center justify-center gap-2 uppercase tracking-widest text-sm">
                            <Target size={16} className="text-purple-600" />
                            Initialize Strategy (5 Credits)
                        </span>
                    </button>
                </div>
            </>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <PremiumPressOverlay isVisible={loading} mode="strategy" currentStepIndex={loadingStepIndex} />
            
            <SectionHeader 
                title="Press Strategy Dossier" 
                icon={Megaphone}
                action={
                    <div className="flex gap-2">
                        <AppButton onClick={handleExportPDF} size="sm" icon={Download} variant="secondary" className="border-zinc-700 bg-black/50">PDF</AppButton>
                        <AppButton onClick={handleGenerateStrategy} size="sm" icon={WandSparkles} variant="secondary" className="border-zinc-700 bg-black/50">Refresh</AppButton>
                    </div>
                }
            />

            {/* Navigation Pills */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-2 custom-scrollbar">
                {[
                    { key: 'executive', label: 'Executive Summary', icon: Star },
                    { key: 'pillars', label: 'Strategy Pillars', icon: Target },
                    { key: 'outlets', label: 'Media Targets', icon: Newspaper },
                    { key: 'timeline', label: 'Campaign Timeline', icon: Calendar },
                    { key: 'metrics', label: 'Success Metrics', icon: BarChart2 }
                ].map(tab => (
                    <TacticalTab 
                        key={tab.key}
                        active={activeStrategyTab === tab.key}
                        onClick={() => setActiveStrategyTab(tab.key)}
                        icon={tab.icon}
                        label={tab.label}
                    />
                ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-1">
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                    
                    {activeStrategyTab === 'executive' && (
                        <div className="space-y-6">
                            <div className="bg-gradient-to-br from-zinc-900/80 to-purple-900/20 border border-purple-500/30 rounded-2xl p-8 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                                    <Megaphone size={120} />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-4 font-poppins">Executive Summary</h3>
                                <p className="text-zinc-200 text-lg leading-relaxed relative z-10">
                                    {pressStrategy.executive_summary}
                                </p>
                            </div>

                            {/* Angles Grid */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {pressStrategy.press_angles?.map((angle, index) => (
                                    <StrategyCard key={index} title={angle.angle} icon={Lightbulb}>
                                        <p className="text-white text-sm font-medium mb-3">{angle.key_message}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {angle.supporting_materials?.map((mat, i) => (
                                                <span key={i} className="px-2 py-1 bg-zinc-800 text-zinc-400 text-[10px] rounded border border-zinc-700">
                                                    {mat}
                                                </span>
                                            ))}
                                        </div>
                                    </StrategyCard>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeStrategyTab === 'pillars' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {pressStrategy.strategy_pillars?.map((pillar, index) => (
                                <StrategyCard key={index} title={pillar.pillar} icon={Target}>
                                    <p className="text-zinc-300 mb-4 text-sm leading-relaxed">{pillar.description}</p>
                                    <div className="space-y-2">
                                        {pillar.tactics?.map((tactic, i) => (
                                            <div key={i} className="flex items-start gap-3 p-2 rounded bg-black/20 border border-white/5">
                                                <CheckCircle className="w-3 h-3 text-purple-500 mt-1 flex-shrink-0" />
                                                <span className="text-xs text-zinc-400">{tactic}</span>
                                            </div>
                                        ))}
                                    </div>
                                </StrategyCard>
                            ))}
                        </div>
                    )}

                    {activeStrategyTab === 'outlets' && (
                        <div className="space-y-8">
                            {['tier_1', 'tier_2', 'niche_specialized'].map((tier) => {
                                const outlets = pressStrategy.target_outlets?.[tier];
                                if (!outlets?.length) return null;
                                return (
                                    <div key={tier}>
                                        <h4 className="text-xs font-bold text-purple-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                                            <div className="w-1 h-4 bg-purple-500 rounded-full"></div>
                                            {tier.replace('_', ' ').toUpperCase()} Targets
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                            {outlets.map((outlet, index) => (
                                                <div key={index} className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-xl hover:border-zinc-700 transition-colors">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <h5 className="font-bold text-white text-sm">{outlet.outlet}</h5>
                                                        <PriorityBadge priority={outlet.priority} />
                                                    </div>
                                                    <div className="space-y-2 text-xs">
                                                        <div className="flex justify-between text-zinc-500">
                                                            <span>Contact:</span>
                                                            <span className="text-zinc-300">{outlet.contact_type}</span>
                                                        </div>
                                                        <div className="p-2 bg-black/30 rounded border border-white/5 text-zinc-400 italic">
                                                            "{outlet.angle}"
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {activeStrategyTab === 'timeline' && (
                        <div className="relative border-l border-zinc-800 ml-4 space-y-8 py-2">
                            {pressStrategy.timeline?.map((phase, index) => (
                                <div key={index} className="relative pl-8">
                                    <div className="absolute -left-[5px] top-2 w-2.5 h-2.5 rounded-full bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.5)]"></div>
                                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4">
                                            <h4 className="font-bold text-white font-poppins">{phase.phase}</h4>
                                            <span className="text-xs font-mono text-purple-400 bg-purple-500/10 px-2 py-1 rounded border border-purple-500/20 w-fit">
                                                {phase.timeframe}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Activations</p>
                                                <ul className="space-y-1">
                                                    {phase.activities?.map((act, i) => (
                                                        <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                                                            <span className="text-zinc-600">›</span> {act}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Deliverables</p>
                                                <ul className="space-y-1">
                                                    {phase.deliverables?.map((del, i) => (
                                                        <li key={i} className="text-xs text-zinc-400 flex items-start gap-2">
                                                            <FileText size={10} className="mt-0.5" /> {del}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {activeStrategyTab === 'metrics' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                             <StrategyCard title="KPIs & Metrics" icon={BarChart2}>
                                <div className="space-y-4">
                                    {pressStrategy.success_metrics?.awareness_goals?.map((goal, i) => (
                                        <div key={i} className="flex justify-between items-center p-3 bg-black/20 rounded border border-white/5">
                                            <span className="text-sm text-zinc-300">{goal}</span>
                                            <Target size={14} className="text-purple-500" />
                                        </div>
                                    ))}
                                </div>
                             </StrategyCard>
                             
                             {pressStrategy.budget_allocation && (
                                <StrategyCard title="Budget Allocation" icon={DollarSign}>
                                    <div className="space-y-4">
                                        {pressStrategy.budget_allocation.map((item, index) => (
                                            <div key={index}>
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-zinc-300">{item.category}</span>
                                                    <span className="text-purple-400 font-mono">{item.percentage}%</span>
                                                </div>
                                                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                                    <div className="h-full bg-purple-500" style={{ width: `${item.percentage}%` }}></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </StrategyCard>
                             )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- EnhancedInfluencerHub ---

const EnhancedInfluencerHub = ({ 
    filmData, setNotification, influencerList, setInfluencerList, 
    addInfluencerToList, updateInfluencerStatus, saveDiscoveredInfluencers, 
    credits, deductCredit, updateInfluencerInDB, db
}) => {
    const [activeInfluencerTab, setActiveInfluencerTab] = useState('discover');
    const [loading, setLoading] = useState(false);
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);
    const [showOutreachModal, setShowOutreachModal] = useState(false);
    const [currentInfluencer, setCurrentInfluencer] = useState(null);
    const [generatedOutreach, setGeneratedOutreach] = useState('');
    const [loadingOutreach, setLoadingOutreach] = useState(false);

    // Derived Lists
    const contactList = useMemo(() => influencerList.filter(inf => inf.film_id === filmData.id && inf.status !== 'discovered'), [influencerList, filmData.id]);
    const discoveredList = useMemo(() => influencerList.filter(inf => inf.film_id === filmData.id && inf.status === 'discovered'), [influencerList, filmData.id]);

    const latestAudienceAnalysis = useMemo(() => {
        if (!filmData.ai_analyses || filmData.ai_analyses.length === 0) return null;
        return filmData.ai_analyses[filmData.ai_analyses.length - 1].analysis;
    }, [filmData.ai_analyses]);

    const handleDiscoverInfluencers = async () => {
        if (!latestAudienceAnalysis) return setNotification({ type: 'info', message: 'Run Audience Analysis first.' });
        if (credits < 2) return setNotification({ type: 'error', message: 'Insufficient credits.' });

        setLoading(true);
        setLoadingStepIndex(0);
        const interval = setInterval(() => setLoadingStepIndex(prev => (prev < 4 ? prev + 1 : prev)), 1500);
        
        try {
            const result = await generateInfluencerListLLM(filmData, latestAudienceAnalysis);
            setLoadingStepIndex(4);
            await new Promise(r => setTimeout(r, 800));
            await deductCredit(2);
            await saveDiscoveredInfluencers(result);
            setNotification({ type: 'success', message: 'Influencers discovered!' });
        } catch (error) {
            setNotification({ type: 'error', message: error.message });
        } finally {
            clearInterval(interval);
            setLoading(false);
        }
    };

    const generatePersonalizedOutreach = async (influencer) => {
        if (credits < 2) {
            setNotification({ type: 'error', message: 'Insufficient credits (2 required).' });
            return;
        }
        setLoadingOutreach(true);
        try {
            const prompt = `Create a personalized, professional outreach message for influencer ${influencer.name} (${influencer.platform}) regarding film "${filmData.title}". Tone: Professional yet approachable. Keep it concise.`;
            const result = await callGeminiApi(prompt);
            setGeneratedOutreach(result);
            await deductCredit(2);
        } catch (error) {
            setNotification({ type: 'error', message: 'Generation failed.' });
        } finally {
            setLoadingOutreach(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const colors = {
            discovered: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
            to_contact: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
            contacted: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
            negotiating: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
            confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
            declined: 'bg-red-500/10 text-red-400 border-red-500/20'
        };
        return (
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${colors[status] || 'bg-zinc-800 text-zinc-500'}`}>
                {status?.replace('_', ' ')}
            </span>
        );
    };

   const InfluencerRow = ({ influencer, isDiscovery }) => {
        const platformInfo = PLATFORMS[influencer.platform?.toLowerCase()];
        const Icon = platformInfo?.brandIcon || Users;
        
        // Restore URL Calculation Logic
        const cleanHandle = influencer.handle?.replace(/^@/, '') || '';
        const visitUrl = platformInfo?.baseUrl ? `${platformInfo.baseUrl}${cleanHandle}` : null;
        
        return (
            <div className="group flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:border-purple-500/30 transition-all gap-4">
                <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${platformInfo?.color || 'bg-zinc-800'} text-white shadow-lg`}>
                        <Icon size={18} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className="font-bold text-white text-sm">{influencer.name}</h4>
                            <span className="text-xs text-zinc-500">@{cleanHandle}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                             <span className="text-[10px] font-mono text-zinc-400">{influencer.followers} Follows</span>
                             {!isDiscovery && <StatusBadge status={influencer.status} />}
                        </div>
                    </div>
                </div>

                <div className="flex-1 w-full sm:w-auto">
                    {influencer.reasoning && (
                        <p className="text-xs text-zinc-400 line-clamp-2 italic border-l-2 border-zinc-700 pl-2">
                            {influencer.reasoning}
                        </p>
                    )}
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center">
                    {/* --- TARGETED FIX: RESTORED VISIT BUTTON --- */}
                    {visitUrl && (
                        <a 
                            href={visitUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white transition-colors border border-transparent hover:border-zinc-700"
                            title="Visit Profile"
                        >
                            <ExternalLink size={16} />
                        </a>
                    )}
                    {/* ------------------------------------------- */}

                    {isDiscovery ? (
                        <button 
                            onClick={() => addInfluencerToList(influencer)}
                            className="p-2 rounded-lg bg-purple-500/10 text-purple-400 hover:bg-purple-500 hover:text-white transition-colors border border-purple-500/20"
                            title="Add to CRM"
                        >
                            <Plus size={16} />
                        </button>
                    ) : (
                        <>
                             {influencer.status === 'to_contact' && (
                                <button 
                                    onClick={() => { setCurrentInfluencer(influencer); setGeneratedOutreach(''); setShowOutreachModal(true); }}
                                    className="p-2 rounded-lg bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                                    title="Generate Outreach"
                                >
                                    <MessageSquare size={16} />
                                </button>
                             )}
                             <select 
                                value={influencer.status}
                                onChange={(e) => updateInfluencerStatus(influencer.id, e.target.value)}
                                className="bg-black/50 border border-zinc-700 text-zinc-300 text-xs rounded-lg px-2 py-1.5 focus:border-purple-500 outline-none"
                             >
                                <option value="to_contact">To Contact</option>
                                <option value="contacted">Contacted</option>
                                <option value="negotiating">Negotiating</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="declined">Declined</option>
                             </select>
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="h-full flex flex-col">
            <PremiumPressOverlay isVisible={loading} mode="influencer" currentStepIndex={loadingStepIndex} />
            
            <SectionHeader title="Influencer Intelligence" icon={Handshake} action={null} />

            <div className="flex justify-between items-center mb-4">
                 <div className="flex gap-2">
                    <TacticalTab 
                        active={activeInfluencerTab === 'discover'} 
                        onClick={() => setActiveInfluencerTab('discover')} 
                        icon={BrainCircuit} 
                        label="AI Discovery"
                        count={discoveredList.length}
                    />
                    <TacticalTab 
                        active={activeInfluencerTab === 'contacts'} 
                        onClick={() => setActiveInfluencerTab('contacts')} 
                        icon={Users} 
                        label="CRM Database"
                        count={contactList.length}
                    />
                </div>
                {activeInfluencerTab === 'discover' && (
                     <button 
                        onClick={handleDiscoverInfluencers}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase rounded-lg hover:scale-105 transition-transform"
                     >
                        <BrainCircuit size={14} className="text-purple-600" />
                        Run Scan (2 Credits)
                     </button>
                )}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-1 space-y-3">
                {activeInfluencerTab === 'discover' ? (
                    discoveredList.length > 0 ? (
                        discoveredList.map((inf, i) => <InfluencerRow key={i} influencer={inf} isDiscovery={true} />)
                    ) : (
                         <div className="text-center py-20 opacity-50">
                            <BrainCircuit size={48} className="mx-auto mb-4 text-zinc-600" />
                            <p className="text-zinc-400">Initialize AI scan to identify partners.</p>
                         </div>
                    )
                ) : (
                    contactList.length > 0 ? (
                        contactList.map((inf, i) => <InfluencerRow key={i} influencer={inf} isDiscovery={false} />)
                    ) : (
                        <div className="text-center py-20 opacity-50">
                            <Users size={48} className="mx-auto mb-4 text-zinc-600" />
                            <p className="text-zinc-400">CRM database empty. Add discovered influencers.</p>
                         </div>
                    )
                )}
            </div>

             {/* Outreach Modal */}
             {showOutreachModal && currentInfluencer && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[60] p-4">
                    <div className="w-full max-w-2xl bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                <MessageSquare className="text-purple-500" size={20} />
                                Outreach Terminal
                            </h3>
                            <button onClick={() => setShowOutreachModal(false)} className="text-zinc-500 hover:text-white"><Plus size={24} className="rotate-45" /></button>
                        </div>

                        {!generatedOutreach ? (
                            <div className="text-center py-8">
                                {loadingOutreach ? (
                                    <div className="flex flex-col items-center">
                                        <Loader2 className="animate-spin text-purple-500 mb-2" size={32} />
                                        <p className="text-zinc-400 text-xs uppercase tracking-widest">Drafting Correspondence...</p>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => generatePersonalizedOutreach(currentInfluencer)}
                                        className="px-6 py-3 bg-white text-black font-bold uppercase rounded-lg hover:bg-zinc-200"
                                    >
                                        Generate Draft (2 Credits)
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <textarea 
                                    className="w-full h-64 bg-black/50 border border-zinc-700 rounded-lg p-4 text-zinc-300 font-mono text-sm resize-none focus:border-purple-500 outline-none"
                                    defaultValue={generatedOutreach}
                                />
                                <div className="flex justify-end gap-2">
                                    <AppButton onClick={() => { navigator.clipboard.writeText(generatedOutreach); setNotification({type:'success', message:'Copied'}); }} icon={Copy} size="sm">Copy</AppButton>
                                    <AppButton onClick={() => setGeneratedOutreach('')} variant="secondary" size="sm">Regenerate</AppButton>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- MAIN PressOutreachTab Component ---

const PressOutreachTab = ({ filmData, user, db, storage, saveFilmData, setNotification, credits, deductCredit }) => {
    const [activeSubTab, setActiveSubTab] = useState('strategy');
    const [isPressKitModalOpen, setIsPressKitModalOpen] = useState(false);
    
    // Press Kit & Data Persistence
    const [pressKitData, setPressKitData] = useState({});
    const [influencerList, setInfluencerList] = useState([]);
    const pressStrategy = useMemo(() => filmData.press_strategy || null, [filmData.press_strategy]);

    useEffect(() => {
        if (!filmData?.id || !db || !user?.uid) return;
        const loadData = async () => {
            const defaults = {
                logline: filmData.logline || '', synopsis: '', director_statement: '',
                cast: [], crew: [], full_credits: [], posterUrl: filmData.poster_a || filmData.posterUrl || '',
                isPublic: false,
            };
            setPressKitData({ ...defaults, ...(filmData.press_kit || {}) });

            const qInfluencers = query(collection(db, 'influencers'), where('user_id', '==', user.uid), where('film_id', '==', filmData.id));
            const influencerDocs = await getDocs(qInfluencers);
            setInfluencerList(influencerDocs.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        };
        loadData();
    }, [filmData, db, user]);

    // Data Helpers
    const handleUpdateInfluencerInDB = async (id, data) => {
        await updateInfluencerInDB(id, data, db);
        setInfluencerList(prev => prev.map(inf => inf.id === id ? { ...inf, ...data } : inf));
    };

   const saveDiscoveredInfluencers = useCallback(async (newlyDiscovered) => {
        if (!filmData?.id || !db || !user?.uid) return;
        try {
            // 1. Delete old 'discovered' influencers for THIS project from Firestore
            const qOldDiscovered = query(collection(db, 'influencers'), where('user_id', '==', user.uid), where('film_id', '==', filmData.id), where('status', '==', 'discovered'));
            const oldDocs = await getDocs(qOldDiscovered);
            const deletePromises = oldDocs.docs.map(doc => deleteDoc(doc.ref));
            await Promise.all(deletePromises);

            // 2. Add the new influencers to Firestore
            const influencersToSave = newlyDiscovered.map(inf => ({
                user_id: user.uid,
                film_id: filmData.id,
                name: inf.name,
                handle: inf.handle,
                platform: inf.platform.toLowerCase(),
                followers: inf.followers,
                reasoning: inf.reasoning,
                outreach_idea: inf.outreach_idea,
                status: 'discovered',
                created_at: new Date(),
                updated_at: new Date()
            }));
            const addPromises = influencersToSave.map(inf => addDoc(collection(db, 'influencers'), inf));
            const addedDocs = await Promise.all(addPromises);

            // 3. Intelligently update the local state without a full re-fetch
            const newInfluencersWithIds = influencersToSave.map((inf, index) => ({
                ...inf,
                id: addedDocs[index].id
            }));

            setInfluencerList(currentList => {
                // Keep all influencers that are NOT in a 'discovered' state for the current project
                const otherInfluencers = currentList.filter(inf => inf.status !== 'discovered' || inf.film_id !== filmData.id);
                // Add the newly discovered ones back in
                return [...otherInfluencers, ...newInfluencersWithIds];
            });

        } catch (error) {
            console.error('Failed to save discovered influencers:', error);
            setNotification({ type: 'error', message: 'There was an error updating the influencer list.' });
        }
    }, [filmData?.id, db, user?.uid, setNotification]);


    const addInfluencerToList = async (infData) => {
        const docRef = await addDoc(collection(db, 'influencers'), {
            user_id: user.uid, film_id: filmData.id, ...infData, status: 'to_contact', created_at: new Date()
        });
        setInfluencerList(prev => [{...infData, id: docRef.id, status: 'to_contact'}, ...prev.filter(i => i.handle !== infData.handle)]);
        setNotification({ type: 'success', message: 'Added to CRM' });
    };

    const updateInfluencerStatus = async (id, status) => {
        setInfluencerList(prev => prev.map(i => i.id === id ? { ...i, status } : i));
        await updateDoc(doc(db, 'influencers', id), { status });
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
            {/* LEFT: Command Sidebar */}
            <div className="lg:col-span-3 flex flex-col h-full gap-4">
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-xl">
                    <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4">Publicity Module</h3>
                    
                    <div className="space-y-2">
                        {[
                            { id: 'strategy', label: 'Strategy Dossier', icon: Megaphone },
                            { id: 'influencer', label: 'Influencer Hub', icon: Handshake },
                            { id: 'kit', label: 'Press Kit Assets', icon: FileText },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all border ${
                                    activeSubTab === tab.id 
                                    ? 'bg-purple-500/10 text-white border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.1)]' 
                                    : 'text-zinc-400 border-transparent hover:bg-zinc-800 hover:text-zinc-200'
                                }`}
                            >
                                <tab.icon size={18} className={activeSubTab === tab.id ? 'text-purple-400' : 'text-zinc-500'} />
                                <span className="font-bold text-sm">{tab.label}</span>
                                {tab.id === 'strategy' && pressStrategy && <CheckCircle size={12} className="ml-auto text-green-500" />}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Project Stats Widget */}
                <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-5 flex-1">
                    <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-4">Live Stats</h4>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-zinc-400">Media Targets</span>
                            <span className="text-white font-mono">{pressStrategy?.target_outlets?.tier_1?.length || 0}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-zinc-400">CRM Contacts</span>
                            <span className="text-white font-mono">{influencerList.filter(i => i.status !== 'discovered').length}</span>
                        </div>
                        <div className="h-px bg-zinc-800 my-2"></div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-zinc-400">Kit Status</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${pressKitData.isPublic ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-zinc-500'}`}>
                                {pressKitData.isPublic ? 'PUBLISHED' : 'DRAFT'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT: Main Interface */}
            <div className="lg:col-span-9 h-full bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl overflow-hidden relative">
                
                {activeSubTab === 'strategy' && (
                    <AIPressStrategyGenerator
                        filmData={filmData}
                        pressKitData={pressKitData}
                        setNotification={setNotification}
                        pressStrategy={pressStrategy}
                        credits={credits}
                        deductCredit={deductCredit}
                        saveFilmData={saveFilmData}
                    />
                )}

                {activeSubTab === 'influencer' && (
                    <EnhancedInfluencerHub
                        filmData={filmData}
                        setNotification={setNotification}
                        influencerList={influencerList}
                        setInfluencerList={setInfluencerList}
                        credits={credits}
                        deductCredit={deductCredit}
                        addInfluencerToList={addInfluencerToList}
                        updateInfluencerStatus={updateInfluencerStatus}
                        saveDiscoveredInfluencers={saveDiscoveredInfluencers}
                        updateInfluencerInDB={handleUpdateInfluencerInDB}
                        db={db}
                        user={user}
                    />
                )}

                {activeSubTab === 'kit' && (
                    <div className="h-full flex flex-col">
                        <SectionHeader title="Press Asset Vault" icon={FileText} action={null} />
                        
                        <div className="flex-1 flex flex-col items-center justify-center relative">
                            {/* Background Elements */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none"></div>

                            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-4xl">
                                <div className="bg-zinc-900/80 border border-zinc-700 p-8 rounded-2xl shadow-xl flex flex-col justify-between hover:border-purple-500/50 transition-all group">
                                    <div>
                                        <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center text-purple-500 mb-6 border border-zinc-800 group-hover:scale-110 transition-transform">
                                            <Layout size={24} />
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-2">EPK Builder</h3>
                                        <p className="text-zinc-400 text-sm leading-relaxed">
                                            Generate industry-standard Electronic Press Kits. Manage bios, synopses, and hi-res assets in one secure link.
                                        </p>
                                    </div>
                                    <AppButton 
                                        onClick={() => setIsPressKitModalOpen(true)}
                                        className="mt-8 w-full"
                                        icon={ExternalLink}
                                    >
                                        Launch Editor
                                    </AppButton>
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-xl flex items-center gap-4">
                                        <div className="p-3 bg-green-500/10 rounded-full text-green-400">
                                            <CheckCircle size={20} />
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold">Cast & Crew Bios</h4>
                                            <p className="text-xs text-zinc-500">{pressKitData.cast?.length || 0} Profiles Active</p>
                                        </div>
                                    </div>
                                    <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-xl flex items-center gap-4">
                                        <div className="p-3 bg-blue-500/10 rounded-full text-blue-400">
                                            <Share2 size={20} />
                                        </div>
                                        <div>
                                            <h4 className="text-white font-bold">Shareable Link</h4>
                                            <p className="text-xs text-zinc-500">{pressKitData.isPublic ? 'Publicly Accessible' : 'Private Mode'}</p>
                                        </div>
                                    </div>
                                    <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-xl flex items-center gap-4 opacity-50">
                                        <div className="p-3 bg-zinc-800 rounded-full text-zinc-500">
                                            <Lock size={20} />
                                        </div>
                                        <div>
                                            <h4 className="text-zinc-300 font-bold">Screener Room</h4>
                                            <p className="text-xs text-zinc-600">Coming in v2.0</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Component */}
            <PressKitBuilderModal 
                isOpen={isPressKitModalOpen}
                onClose={() => setIsPressKitModalOpen(false)}
                filmData={filmData}
                db={db}
                storage={storage}
                user={user}
                setNotification={setNotification}
                saveFilmData={saveFilmData}
                initialPressKitData={pressKitData} 
            />
        </div>
    );
};

export default PressOutreachTab;